import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, Factory, Lightbulb, Wifi, CheckCircle, TrendingUp, AlertTriangle, BarChart3, Zap, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const services = [
  {
    icon: ShoppingCart,
    name: 'Retail',
    image: '/images/retail-ai.webp',
    tagline: 'Transforming customer experiences with intelligent commerce.',
    offerings: [
      'Customer Segmentation & Value Profiling',
      'Propensity Modeling & Campaign Response',
      'Market Mix Modelling & Promotion Effectiveness',
      'Demand Forecasting & Replenishment Strategy',
      'Size Profiling & Pack Optimization',
      'Market Basket Analysis & Assortment Planning',
      'Customer Churn Identification',
      'Non-Performing Inventory & Reallocation'
    ],
    results: ['5-10% improvement in weekly sales', '$15M increase in GMROI', 'Lost sales reduced from 62% to 9%'],
    caseStudies: [
      {
        title: 'Demand Forecasting & Size Profiling for Apparel Retailer',
        region: 'USA',
        problem: 'A large US apparel retailer suffered from chronic stockouts and overstock driven by inaccurate demand planning and suboptimal size-pack configurations, with lost sales running as high as 62% in certain categories.',
        solution: 'Designed a time-series forecasting model at SKU-store-week level using transaction data, promotional calendars, and inventory receipts. Layered a simulation engine to test different size-pack configurations, minimizing lost sales while maximizing sell-through rates.',
        impact: ['Lost sales reduced from 62% to 9% (best-case)', 'Average lost sales reduction of 31% across categories', '$15M increase in GMROI from fewer markdowns', '5-10% improvement in weekly sales'],
        icon: BarChart3
      },
      {
        title: 'Customer Segmentation & Campaign Response Optimization',
        region: 'India',
        problem: 'A retailer\'s marketing team ran broad, untargeted campaigns with low response rates and high per-capita costs. They lacked a data-driven framework to identify which customers to target for maximum ROI within a fixed campaign budget.',
        solution: 'Built a two-stage modeling pipeline: a statistical segmentation model for value-based customer profiling, followed by an uplift model combining purchase propensity and redemption likelihood to rank customers by incremental campaign responsiveness.',
        impact: ['Significant marketing cost savings through precision targeting', 'Improved campaign response rates and profitability', 'Maximized cross-selling and upselling opportunities', 'Real-time customer profiling engine for segmented comms'],
        icon: Target
      },
      {
        title: 'Non-Performing Inventory Identification & Reallocation',
        region: 'USA',
        problem: 'A broad-line US retailer carried excess non-productive inventory across stores, tying up working capital and leading to frequent markdowns. Simultaneously, high-performing stores faced stockouts on the same products.',
        solution: 'Developed a statistical model mapping ideal demand curves against actual inventory response curves to identify NPIs. Built a cost-optimized inter-store reallocation framework that transfers excess inventory to high-demand locations, subject to transportation and operational constraints.',
        impact: ['$3M worth of non-performing inventory identified and actioned', '90-96% fulfillment of unmet demand via reallocation', 'Reduced markdown situations improving top and bottom line', 'Improved customer satisfaction through better availability'],
        icon: TrendingUp
      }
    ]
  },
  {
    icon: Factory,
    name: 'Manufacturing',
    image: '/images/manufacturing-ai.webp',
    tagline: 'Building the intelligent factory of tomorrow.',
    offerings: [
      'Predictive Maintenance & Asset Management',
      'Quality Control with Computer Vision',
      'Production Scheduling Optimization',
      'Digital Twin Development',
      'Robotics & Automation Intelligence',
      'Energy Efficiency Optimization'
    ],
    results: ['50% reduction in unplanned downtime', '30% improvement in production efficiency', '40% decrease in quality defects'],
    caseStudies: [
      {
        title: 'Predictive Maintenance for Heavy Equipment',
        region: 'Europe',
        problem: 'A steel manufacturer experienced frequent unplanned shutdowns of critical rotating equipment (compressors, turbines), each costing $200K+ in lost production and emergency repairs.',
        solution: 'Ingested vibration, thermal, acoustic, and process data from 1,200+ sensors. Trained survival analysis and gradient-boosted models to predict failure windows 2-6 weeks in advance, integrated with the existing CMMS for automated work-order generation.',
        impact: ['50% reduction in unplanned downtime', '30% extension of average equipment life', '$8.5M annual savings in maintenance costs'],
        icon: AlertTriangle
      },
      {
        title: 'Manufacturing 4.0: Digital Twin & Smart Factory Rollout',
        region: 'Germany',
        problem: 'A multi-plant automotive manufacturer operated with siloed MES, ERP, and SCADA systems, making it impossible to get a unified real-time view of production. Operators relied on lagging KPIs, leading to slow responses to quality drifts, energy waste, and unplanned line stoppages across 12 production lines.',
        solution: 'Architected a digital twin of the entire production floor by integrating IoT sensor streams, MES events, ERP schedules, and energy meter data into a unified data lake. Built real-time dashboards with anomaly detection agents that flag deviations in cycle time, energy per unit, and quality parameters. Deployed closed-loop ML models that auto-adjust machine setpoints for optimal throughput-quality-energy tradeoffs.',
        impact: ['18% improvement in Overall Equipment Effectiveness (OEE)', '22% reduction in energy consumption per unit produced', 'Real-time visibility across 12 lines, replacing 48-hour lag', '35% faster root-cause identification for quality incidents'],
        icon: Target
      },
      {
        title: 'Production Yield Optimization',
        region: 'India',
        problem: 'A petrochemical plant experienced significant yield variability across production batches, with operators relying on experience-based adjustments that often missed optimal operating windows.',
        solution: 'Built a real-time process optimization model combining physics-based constraints with ML predictions. The system recommends optimal setpoints for temperature, pressure, and catalyst ratios, accounting for feedstock variability.',
        impact: ['3-5% improvement in production yield', '$12M annual value from optimized throughput', 'Reduced batch-to-batch variability by 60%'],
        icon: TrendingUp
      }
    ]
  },
  {
    icon: Lightbulb,
    name: 'Energy & Utilities',
    image: '/images/energy-utilities-ai.webp',
    tagline: 'Powering the clean energy transition with AI.',
    offerings: [
      'Asset Failure Prediction & Predictive Maintenance',
      'Spare Parts Inventory Optimization',
      'Plant Operations & Yield Optimization',
      'Energy Planning & Portfolio Management',
      'Electricity Price Modelling & Trading Strategy',
      'Renewable Integration & Grid Balancing'
    ],
    results: ['8-12% reduction in maintenance costs', '15-25% lower inventory carrying costs', '3-5% yield improvement'],
    caseStudies: [
      {
        title: 'Asset Failure Prediction for Power Generation',
        region: 'India',
        problem: 'A power generation company faced recurring unplanned outages from critical asset failures in turbines, compressors, and auxiliary equipment -- each event causing costly emergency repairs and significant revenue loss from reduced Plant Load Factor (PLF).',
        solution: 'Ingested vibration, temperature, pressure, acoustic, and resource consumption data alongside historical breakdown logs. Built an ensemble framework combining survival models (Cox Hazards, Accelerated Failure Time), classification models for failure probability at multiple horizons, anomaly detection on resource utilization, and causal inference on process parameters -- all calibrated via out-of-sample performance.',
        impact: ['8-12% reduction in maintenance and procurement costs', 'Significant decrease in unplanned downtime', 'Improved reliability and Plant Load Factor', 'Enhanced safety and compliance through early risk identification'],
        icon: AlertTriangle
      },
      {
        title: 'Spare Parts Inventory Optimization',
        region: 'Middle East',
        problem: 'An energy operator experienced extended downtime from unavailability of critical spare parts, while simultaneously holding years of unused stock on non-critical items -- inflating capex and straining working capital without improving asset uptime.',
        solution: 'Classified spares using combined ABC-VED-FSN analysis into priority SKU-classes. Developed a class-based probabilistic forecasting framework for demand quantiles and safety-stock sizing. Built an LP/MILP optimization engine factoring criticality, lead times, downtime costs, and target fill rates. Validated robustness via Monte Carlo simulation of demand and supply disruption scenarios.',
        impact: ['15-25% reduction in inventory carrying costs', '20-30% improvement in critical spare availability', 'Lower forced outage hours from stockouts', 'Faster response to demand variability and supply disruptions'],
        icon: BarChart3
      },
      {
        title: 'Day-Ahead Electricity Price Forecasting',
        region: 'Europe',
        problem: 'Highly volatile and unpredictable day-ahead market prices exposed the operator to significant financial risk, complicating fuel procurement, bid submissions, and unit dispatch decisions -- leading to last-minute premium fuel purchases and overly conservative scheduling.',
        solution: 'Implemented a dynamic regression (transfer-function) model using hourly load, generation-mix indicators, fuel-price proxies, weather variables, and calendar features to explain next-day hourly prices. Fitted ARIMA/SARIMA models on residuals to capture autocorrelation and seasonal structure. Automated data ingestion with scheduled retraining, producing hourly day-ahead price vectors integrated with dispatch and bidding systems.',
        impact: ['MAPE of ~3.5% on day-ahead price forecasts', 'Reduced imbalance penalties and fuel costs', 'Optimal unit commitment lowering cycling costs', 'Market-aware trading enabling peak/off-peak hedges'],
        icon: TrendingUp
      }
    ]
  },
  {
    icon: Wifi,
    name: 'Telecom',
    image: '/images/telecom-ai.webp',
    tagline: 'Enabling next-generation network intelligence.',
    offerings: [
      'Network Optimization & Self-Healing',
      '5G Network Slicing Intelligence',
      'Customer Churn Prediction & Prevention',
      'Intelligent Traffic Management',
      'Fraud Detection & Prevention',
      'Revenue Assurance Analytics'
    ],
    results: ['35% improvement in network efficiency', '25% reduction in customer churn', '40% faster incident resolution'],
    caseStudies: [
      {
        title: 'Customer Churn Prediction & Retention',
        region: 'Africa',
        problem: 'A tier-1 mobile operator with 45M+ subscribers faced 4.2% monthly churn, with retention campaigns deployed too late and too broadly, yielding poor ROI on $30M annual retention spend.',
        solution: 'Built a multi-signal churn model ingesting CDR patterns, data usage trends, complaint history, network experience scores, and competitive pricing intelligence. Integrated with campaign management for automated, persona-based interventions 30 days before predicted churn.',
        impact: ['25% reduction in monthly churn rate', '3.5x improvement in retention campaign ROI', '$18M annual savings in reduced churn revenue loss'],
        icon: Target
      },
      {
        title: 'Network Anomaly Detection & Self-Healing',
        region: 'Southeast Asia',
        problem: 'A converged telecom operator managing 80,000+ network elements experienced 15,000+ daily alarms, with NOC teams overwhelmed by false positives and unable to identify root causes before customer impact.',
        solution: 'Deployed an AI-powered alarm correlation engine using graph neural networks to map network topology dependencies, suppress correlated alarms, identify root cause, and trigger automated remediation scripts for known failure patterns.',
        impact: ['85% reduction in actionable alarm volume', '40% faster mean time to resolution (MTTR)', '60% of common faults auto-remediated without human intervention'],
        icon: Zap
      },
      {
        title: 'Revenue Assurance & Fraud Detection',
        region: 'Latin America',
        problem: 'A mobile operator estimated 3-5% revenue leakage from billing errors, SIM box fraud, subscription fraud, and interconnect discrepancies, but lacked the analytical capability to detect and quantify losses in real time.',
        solution: 'Implemented a real-time fraud detection platform using unsupervised anomaly detection on CDR streams, combined with supervised models trained on historical fraud cases. The system flags suspicious patterns within minutes and auto-blocks high-confidence fraud.',
        impact: ['$22M annual revenue recovered from leakage', '90% of SIM box fraud detected within 2 hours', '2.1% improvement in net revenue realization'],
        icon: BarChart3
      }
    ]
  }
];

function CaseStudyCard({ cs, CsIcon, index }) {
  const [expanded, setExpanded] = useState(false);
  const problemPreview = cs.problem.length > 175 ? `${cs.problem.slice(0, 172).trim()}...` : cs.problem;
  const impactItems = expanded ? cs.impact : cs.impact.slice(0, 2);

  return (
    <div
      data-testid={`case-study-${index}`}
      className="border border-border rounded-sm overflow-hidden bg-white hover:shadow-lg transition-shadow duration-300"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="w-10 h-10 rounded-sm bg-teal/10 flex items-center justify-center">
            <CsIcon className="h-5 w-5 text-teal" />
          </div>
          <Badge variant="secondary" className="bg-navy/5 text-navy/50 border-0 text-xs rounded-full">
            {cs.region}
          </Badge>
        </div>
        <h4 className="font-heading text-base font-medium text-navy mb-3 leading-snug">{cs.title}</h4>

        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-accent-orange mb-1.5">
            {expanded ? 'Business Problem' : 'Problem Snapshot'}
          </p>
          <p className="text-xs text-navy/55 leading-relaxed">{expanded ? cs.problem : problemPreview}</p>
        </div>

        {expanded && (
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-teal mb-1.5">Solution</p>
            <p className="text-xs text-navy/55 leading-relaxed">{cs.solution}</p>
          </div>
        )}

        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-navy mb-2">
            {expanded ? 'Impact' : 'Key Impact'}
          </p>
          <div className="space-y-1.5">
            {impactItems.map((imp, j) => (
              <div key={j} className="flex items-start gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-xs text-navy/70 font-medium">{imp}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="mt-4 text-xs font-medium text-teal hover:text-teal/80 transition-colors flex items-center gap-1"
        >
          {expanded ? 'Show less' : 'View solution & impact'}
          <ArrowRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <div>
      {/* Hero */}
      <section data-testid="services-hero" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Services by Industry</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.1] mb-6">
            AI Solutions Tailored<br />to Your Industry
          </h1>
          <p className="text-base md:text-lg text-navy/60 max-w-2xl leading-relaxed">
            We combine deep industry expertise with cutting-edge AI capabilities to deliver solutions that address the unique challenges of your sector.
          </p>
        </div>
      </section>

      {/* Industry Sections */}
      {services.map((svc, i) => {
        const Icon = svc.icon;
        const isEven = i % 2 === 0;
        return (
          <div key={svc.name}>
            <section
              data-testid={`service-section-${svc.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`py-24 md:py-32 ${isEven ? 'bg-white' : 'bg-secondary'}`}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={!isEven ? 'lg:order-2' : ''}>
                    <div className="aspect-[4/3] rounded-sm overflow-hidden">
                      <img src={svc.image} alt={svc.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className={!isEven ? 'lg:order-1' : ''}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-sm bg-teal/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-teal" />
                      </div>
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal">{svc.name}</p>
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy mb-4">
                      {svc.tagline}
                    </h2>
                    <div className="space-y-3 mb-8">
                      {svc.offerings.map(o => (
                        <div key={o} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-navy/70">{o}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-navy/5 rounded-sm p-6 mb-8">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-accent-orange mb-3">Proven Results</p>
                      <div className="space-y-2">
                        {svc.results.map(r => (
                          <p key={r} className="text-sm text-navy/80 font-medium">{r}</p>
                        ))}
                      </div>
                    </div>
                    <Link to="/contact">
                      <Button className="bg-navy text-white hover:bg-navy/90 rounded-full px-6">
                        Discuss Your {svc.name} Needs <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Case Studies */}
                <div className="mt-20">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="h-px flex-1 bg-border" />
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-navy/40 px-4">
                      Case Studies - {svc.name}
                    </p>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {svc.caseStudies.map((cs, ci) => {
                      const CsIcon = cs.icon;
                      return (
                        <CaseStudyCard key={ci} cs={cs} CsIcon={CsIcon} index={ci} />
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          </div>
        );
      })}
    </div>
  );
}
